import React from 'react';

import { Gift, HandCoins, OctagonAlert } from 'lucide-react';

import type { ActionMenuItem } from 'components/UI/ActionMenu';

/**
 * THE canonical set of discretionary certificate actions — one declaration, read
 * by every surface that offers them.
 *
 * ## Why this exists rather than a second array
 *
 * The certificate page's kebab and the certificates list's bulk bar are the same
 * menu asked of one record and of N, and they were not the same menu: the page
 * offered Gift and Report lost or stolen, while the bar offered a single
 * "Transfer" whose handler was a `TODO` that silently did nothing. Two
 * hand-maintained lists is how that happens, and re-typing the page's list into
 * the bar would only reset the clock — the two would diverge again on the day a
 * third action lands, which is a day this file's own `sell` entry says is coming.
 *
 * So LABEL, ICON and ORDER live here and nowhere else. What each surface still
 * owns is AVAILABILITY (`utils/certificateActions` answers that for a selection;
 * `isIdleOwned` answers it for a record) and the handler, because those are
 * genuinely per-surface: gifting from a list of five is not the same question as
 * gifting the record you are looking at.
 *
 * ## Order
 *
 * Affirmative first. `Report lost or stolen` is rare and negative, so it sits
 * LAST — position carries the de-emphasis, and it is NOT `destructive` red: it
 * changes a status, it does not destroy the record (§38's test).
 */
type CertificateActionDef = {
  key: CertificateActionKey;
  label: string;
  /**
   * The SHORT form, for a surface that gives an action a column rather than a row
   * — today the certificate page's tile row.
   *
   * It is declared here rather than derived (a `label.split(' ')[0]`) because the
   * two are different editorial decisions that happen to coincide twice out of
   * three: `Report lost or stolen` shortens to `Report`, and a rule that produced
   * that from the full string would also silently produce `Report` from a future
   * `Report a problem`. It is also declared here rather than at the call site, for
   * the reason this whole module exists — a second surface would otherwise retype
   * it and the two would drift.
   *
   * ⚠️ It must stay ONE WORD. The row is four tiles wide at most and the page's
   * content column is 343px at 375 (measured), so a four-tile row gives each tile
   * ~79px of which ~63px is inside the padding — enough for one word at 13px and
   * nothing more. The FULL `label` is what the sheet and the bulk bar render; the
   * short one never leaves the tile.
   */
  tileLabel: string;
  icon: React.ReactNode;
  /**
   * Declared, not yet built. Forces `disabled` plus a "Coming soon" line at every
   * call site that includes it, so the treatment cannot be got right in one place
   * and wrong in another.
   */
  comingSoon?: boolean;
};

/**
 * ⚠️ These are CAPABILITIES, not to-dos, and the distinction decides what may be
 * added here (VERA-304).
 *
 * A capability is standing, repeatable, and taken whenever the owner chooses —
 * Gift, Sell, Report. A to-do is outstanding, terminal and self-clearing, and it
 * belongs in a state card on the page rather than in a menu of things you may do.
 *
 * `verifyPossession` WAS an entry here and was removed for exactly that reason;
 * it is now `VerifyPossessionCard` in the certificate page's ranked state stack.
 * See that file before adding anything one-shot to this list.
 */
export type CertificateActionKey = 'gift' | 'sell' | 'reportMissing';

/**
 * Icons are sized 20 / strokeWidth 1.5 — the certificate page's existing sheet
 * sizing. `ActionMenu` re-clones them to `PICKER_SHEET.iconSize` when it renders
 * as a sheet, so this size only has to be right for the dropdown.
 */
const CERTIFICATE_ACTION_DEFS: CertificateActionDef[] = [
  /*
   * VERA-304: `verifyPossession` was the first entry here, and its own note argued
   * it earned that slot because it "DISAPPEARS once taken… a one-off task the owner
   * completes and never sees again". That is a correct description and the wrong
   * conclusion — a one-off task is not a member of this set at all. It is now a
   * state card (`VerifyPossessionCard`). Gift resumes the first slot permanently.
   */
  {
    key: 'gift',
    label: 'Gift',
    tileLabel: 'Gift',
    icon: <Gift size={20} strokeWidth={1.5} />,
  },
  {
    key: 'sell',
    label: 'Sell',
    tileLabel: 'Sell',
    // Not `Tag` — in this product a tag is the NFC chip, and reusing that glyph
    // for a sale would read as "sell the tag". Not a currency badge either: the
    // app is multi-currency and a `$` would be wrong for most tenants. Coins
    // changing hands is the one that says "transfer for money" without either.
    icon: <HandCoins size={20} strokeWidth={1.5} />,
    /*
     * No longer `comingSoon`. The tile opens the method chooser (List for sale /
     * Sell direct) so the flow can be demonstrated on a real certificate.
     *
     * ⚠️ The chooser is the ONLY part that is live. Neither method completes:
     * the listing, offer, Stripe Connect and escrow machinery is VERA-632 and is
     * NOT on main. The "coming soon" has moved one level deeper, where it names
     * the specific path rather than refusing the whole action.
     */
  },
  {
    key: 'reportMissing',
    label: 'Report lost or stolen',
    // `Report`, not `Lost` or `Missing`: the tile row is a row of VERBS (Gift,
    // Sell, Report) and a noun in the third slot would read as a state rather
    // than as something you do. The full wording survives one tap away, on the
    // drawer this opens.
    tileLabel: 'Report',
    icon: <OctagonAlert size={20} strokeWidth={1.5} />,
  },
];

/**
 * A built row — an `ActionMenuItem` plus the fields only a TILE surface reads.
 *
 * It is a superset, not a parallel type, so one built array feeds both consumers
 * without a map between them: `CertificateActionItem[]` is assignable to
 * `ActionMenuItem[]`, so `ActionMenu` takes it unchanged and simply ignores the
 * extra fields. That assignability is the whole design — the certificate page
 * builds ONE `manageItems` and hands the same array to the tile row and to the
 * overflow sheet, which is what guarantees the two can never offer different
 * actions (the failure this module was created to end).
 */
export type CertificateActionItem = ActionMenuItem & {
  key: CertificateActionKey;
  tileLabel: string;
};

/** Everything a surface decides for itself about one action. */
export interface CertificateActionState {
  onClick?: () => void;
  /** Omit to show it; `false` hides the row (§46 — record state hides). */
  visible?: boolean;
  /** Shown but inert. MUST be paired with a `description` or `tooltip` (§46). */
  disabled?: boolean;
  /**
   * A second line under the label, naming why the row is unavailable.
   *
   * This is the mobile-reachable half of §46's forcing function: a `tooltip`
   * needs hover, and this app is used by tapping an NFC tag with a phone, so a
   * tooltip-only explanation does not exist for most of its users.
   */
  description?: React.ReactNode;
  tooltip?: React.ReactNode;
}

/**
 * Build the menu rows for one surface.
 *
 * An action ABSENT from `states` is omitted entirely — that is how the
 * certificate page keeps `sell` off its kebab while the bulk bar shows it: not
 * every surface has to adopt an action on the day it is declared. Availability
 * diverging per surface is legitimate; label / icon / order diverging is the
 * defect this module prevents.
 */
export const buildCertificateActionItems = (
  states: Partial<Record<CertificateActionKey, CertificateActionState>>,
): CertificateActionItem[] =>
  CERTIFICATE_ACTION_DEFS.flatMap(def => {
    const state = states[def.key];
    if (!state) return [];

    /*
     * COMING SOON is a THIRD case, and §46 covers only two of them.
     *
     * §46 splits an unavailable action by who resolves it: a PERMISSION block is
     * shown disabled with a tooltip naming the permission, because resolution is
     * someone else's, asynchronously, and the disabled row is the only channel
     * that says the capability exists. RECORD STATE hides, because resolution is
     * the user's and the app owes them the resolving action rather than a
     * tombstone.
     *
     * "Coming soon" is neither: nobody can resolve it — not the user, not an
     * admin — so by §46's own logic it should hide, the way `certificate_delete`
     * hides for a company user (§46's stated carve-out for a block nobody in that
     * context can ever hold).
     *
     * It is shown anyway, and the reason it is not the same case is that §46's
     * carve-out is about a permission that will never be GRANTED to this user,
     * where a disabled row is a false promise. This is a capability that does not
     * exist YET, for anyone, and saying so is true rather than a promise the
     * product cannot keep. The label is what makes the difference: an undated
     * disabled row is indistinguishable from a broken one — which is precisely
     * the defect being fixed here, a bulk action that looked live and did nothing
     * — while "Coming soon" reads as deliberate. Hiding it would also lose the
     * one honest signal a user has that selling is on the way at all.
     *
     * The forcing function still holds: this row is disabled AND carries the
     * sentence saying why, on the row itself rather than in a hover.
     */
    const comingSoon = Boolean(def.comingSoon);

    return [
      {
        key: def.key,
        label: def.label,
        tileLabel: def.tileLabel,
        icon: def.icon,
        onClick: state.onClick ?? (() => {}),
        visible: state.visible,
        disabled: comingSoon || state.disabled,
        description: state.description ?? (comingSoon ? 'Coming soon' : undefined),
        tooltip: state.tooltip,
      } satisfies CertificateActionItem,
    ];
  });
